// House — the About-scene landmark. A small house: box body 0.4x0.4x0.4,
// cone roof 0.5h, small chimney. Warm white walls, mint roof. A small
// point light sits inside to suggest "someone is home".
//
// When `isFar` is true (camera further than ~6 from the landmark),
// the inner decorative elements (chimney, window, point light) are
// dropped to spare the render list. The silhouette box + roof stay —
// they're the readable part.

import { useMemo } from 'react';
import * as THREE from 'three';
import type { Group, Mesh } from 'three';

export interface HouseProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  /** When this scene is active the landmark glows. */
  isActive?: boolean;
  /** When hovered the landmark gets a small scale boost. */
  isHovered?: boolean;
  /** When the camera is farther than the LOD threshold, render the
   *  simplified silhouette-only version (no chimney, no window, no
   *  point light). */
  isFar?: boolean;
  /** Group ref the parent can use to register pointer events. */
  groupRef?: React.MutableRefObject<Group | null>;
}

const COLOR_WALL = '#f5f0e8'; // warm white
const COLOR_ROOF = '#7fb89f'; // mint
const COLOR_CHIMNEY = '#5a5a5a'; // muted
const COLOR_LIGHT = '#f5f0e8'; // warm

export function House({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  isActive = false,
  isHovered = false,
  isFar = false,
  groupRef,
}: HouseProps) {
  // Combined scale: base * hover * active.
  const combinedScale = useMemo(() => {
    const base = scale;
    const hover = isHovered ? 1.08 : 1;
    const active = isActive ? 1.15 : 1;
    return base * hover * active;
  }, [scale, isHovered, isActive]);

  return (
    <group
      ref={groupRef as React.MutableRefObject<Group>}
      name="landmark-house"
      position={position}
      rotation={rotation}
      scale={combinedScale}
    >
      {/* Body — box 0.4 x 0.4 x 0.4. Sits with its base on y=0. */}
      <mesh name="house-body" position={[0, 0.2, 0]} userData={{ landmarkType: 'house' }} frustumCulled>
        <boxGeometry args={[0.4, 0.4, 0.4]} />
        <meshStandardMaterial color={COLOR_WALL} roughness={0.85} flatShading />
      </mesh>

      {/* Roof — cone, 0.25 radius at base, 0.5 height. Sits on top of
          the body. */}
      <mesh name="house-roof" position={[0, 0.65, 0]} userData={{ landmarkType: 'house' }} frustumCulled>
        <coneGeometry args={[0.32, 0.5, 4]} />
        <meshStandardMaterial
          color={COLOR_ROOF}
          emissive={COLOR_ROOF}
          emissiveIntensity={isFar ? 0 : 0.15}
          roughness={0.7}
          flatShading
        />
      </mesh>

      {/* Chimney — small box on the back of the roof. Dropped when
       *  the camera is far. */}
      {!isFar && (
        <mesh
          name="house-chimney"
          position={[0.12, 0.78, -0.08]}
          userData={{ landmarkType: 'house' }}
          frustumCulled
        >
          <boxGeometry args={[0.08, 0.18, 0.08]} />
          <meshStandardMaterial color={COLOR_CHIMNEY} roughness={0.95} flatShading />
        </mesh>
      )}

      {/* Window — tiny emissive square on the front face. Suggests
          "lit from within" without a real light. Dropped when far. */}
      {!isFar && (
        <mesh name="house-window" position={[0, 0.25, 0.21]} frustumCulled>
          <planeGeometry args={[0.12, 0.12]} />
          <meshBasicMaterial color={COLOR_LIGHT} transparent opacity={0.9} />
        </mesh>
      )}

      {/* Warm interior point light. Subtle — just a hint. Dropped
       *  when the camera is too far to notice it. */}
      {!isFar && (
        <pointLight
          color={COLOR_LIGHT}
          intensity={isActive ? 0.8 : 0.5}
          distance={2}
          position={[0, 0.25, 0]}
        />
      )}
    </group>
  );
}

export default House;
